import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Plane, X, ArrowRight, Zap } from "lucide-react";
import axios from "axios";
import { API } from "../config";
import { fetchGeo, touristContext, browserIsSpanish } from "../lib/geoContext";

const isSpainPlan = (p) => p.countries?.length === 1 && ["españa", "spain", "espana"].includes(String(p.countries[0]).toLowerCase());

const KEY = "lw_airport_banner_dismissed_at";
const HIDE_DAYS = 7;
const EXCLUDED = ["/admin", "/p/", "/travel/canarias", "/agencias", "/checklist", "/area-clientes"];

// Banner para viajeros: cerca de un aeropuerto canario, IP extranjera o navegador en otro idioma → eSIM Canarias
export const AirportEsimBanner = () => {
  const [ctx, setCtx] = useState(null);
  const [minPrice, setMinPrice] = useState(null);
  const [hidden, setHidden] = useState(() => {
    try { const at = Number(localStorage.getItem(KEY) || 0); return at && Date.now() - at < HIDE_DAYS * 86400000; } catch { return false; }
  });
  const [cookiesAnswered, setCookiesAnswered] = useState(() => { try { return !!localStorage.getItem("livewifi_cookie_consent"); } catch { return true; } });
  const { pathname } = useLocation();

  useEffect(() => {
    if (cookiesAnswered) return;
    const t = setInterval(() => { try { if (localStorage.getItem("livewifi_cookie_consent")) setCookiesAnswered(true); } catch {} }, 1000);
    return () => clearInterval(t);
  }, [cookiesAnswered]);

  useEffect(() => {
    if (hidden) return;
    fetchGeo().then(geo => {
      const c = touristContext(geo);
      setCtx(c);
      if (c) axios.get(`${API}/travel/plans`).then(r => {
        const prices = (r.data?.plans || []).filter(isSpainPlan).map(p => Number(p.price)).filter(Boolean);
        if (prices.length) setMinPrice(Math.min(...prices));
      }).catch(() => {});
    });
  }, [hidden]);

  if (hidden || !cookiesAnswered || !ctx || EXCLUDED.some(p => pathname.startsWith(p))) return null;

  const en = !browserIsSpanish();
  const ap = ctx.airport;
  const src = ap ? `geo-${ap.code}` : ctx.reason;
  const to = `/travel/canarias?src=${src}${en ? "&lang=en" : ""}`;
  const title = ap
    ? (en ? `Just landed in ${ap.island}?` : `¿Acabas de aterrizar en ${ap.island}?`)
    : (en ? "Travelling to the Canary Islands?" : "¿Viajas a Canarias?");
  const price = minPrice ? minPrice.toFixed(2).replace(".", ",") : null;
  const sub = en
    ? `Mobile data eSIM${price ? ` from €${price}` : ""} · QR activation in 2 min · no roaming fees`
    : `eSIM con datos${price ? ` desde ${price} €` : ""} · activación por QR en 2 min · sin roaming`;
  const cta = en ? "Get my eSIM" : "Quiero mi eSIM";
  const dismiss = () => { try { localStorage.setItem(KEY, String(Date.now())); } catch {} setHidden(true); };

  return (
    <div className="fixed bottom-0 inset-x-0 z-[65] px-3 pb-3 sm:px-6 sm:pb-5 pointer-events-none" data-testid="airport-esim-banner" data-src={src}>
      <div className="pointer-events-auto mx-auto max-w-2xl rounded-2xl border border-[#7DD500]/40 bg-[#0E0E0E]/95 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] p-4 sm:p-5 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="hidden sm:flex w-12 h-12 rounded-xl bg-[#7DD500]/15 items-center justify-center shrink-0">
          <Plane className="w-6 h-6 text-[#7DD500]" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-black text-sm sm:text-base leading-tight" data-testid="airport-banner-title">{title}</div>
          <div className="text-white/55 text-xs mt-1 flex items-center gap-1.5"><Zap className="w-3 h-3 text-[#7DD500] shrink-0" />{sub}</div>
        </div>
        <Link to={to} onClick={dismiss} data-testid="airport-banner-cta"
          className="shrink-0 px-4 py-2.5 rounded-full bg-[#7DD500] text-[#0A0A0A] font-black text-xs sm:text-sm inline-flex items-center gap-1.5 hover:bg-[#95E619] transition-colors whitespace-nowrap">
          {cta} <ArrowRight className="w-4 h-4" />
        </Link>
        <button onClick={dismiss} aria-label={en ? "Close" : "Cerrar"} data-testid="airport-banner-close" className="shrink-0 p-1.5 rounded-full text-white/40 hover:text-white hover:bg-white/10">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
